<template>
  <div class="flex-wrapper-app">
    <Navbar />
    <div class="merchantdrop-container-main">
      <div class="container">
        <div v-if="isSuccess === true">
          <MerchantDropoffSuccess :barcodeId="''" :reason="''" />
        </div>
        <div v-else-if="isSuccess === false">
          <LockerReturnFail :errorKey="errorKey" />
        </div>
        <div v-else>
          <h2 class="merchantdrop-title">{{ $t('lockerReturn.title') }}</h2>
          <br />
          <div
            :class="[
              'merchantdrop-main-container',
              { 'merchantdrop-main-container-loading': isLoading },
            ]"
          >
            <div :class="['loader-overlay', { 'loader-overlay-hide': !isLoading }]">
              <Loader />
            </div>
            <div class="font-size-20">
              <div class="merchantdrop-grid-table">
                <h3>
                  {{ $t('lockerReturn.selectPointLabel') }}
                </h3>

                <button @click="showMap = true && isMobile" class="show-map-button">
                  {{ $t('lockerReturn.showPointsButton') }}
                </button>

                <div v-show="showMap || !isMobile" class="popup-place-slm-map-container">
                  <div class="popup-place-slm-map" style="text-align:right">
                    <span class="closeMapBtn" @click="closeMap" v-if="isMobile">ⓧ</span>
                    <div id="skroutzLockerMap" class="merchantdrop-map"></div>
                  </div>
                </div>
              </div>
            </div>
            <div
              :class="[
                'margin-top-10',
                {
                  invisible: !(
                    selectedLockerExternalId &&
                    selectedLockerFriendlyName &&
                    selectedLockerAddress &&
                    !isLoading
                  ),
                },
              ]"
            >
              <p class="merchantdrop-selected-locker-message">
                {{ $t('lockerReturn.selectedPointPrefix') }}
                <strong class="merchantdrop-selected-highlight">{{
                  selectedLockerFriendlyName
                }}</strong>
                {{ $t('lockerReturn.atAddress') }}
                <strong class="merchantdrop-selected-highlight">{{ selectedLockerAddress }}</strong>
              </p>
              <div class="slot-size-container">
                <h3 class="font-size-20">
                  {{ $t('lockerReturn.selectSizeLabel') }}
                </h3>
                <br />

                <button-selector
                  :availableSlotSizes="this.availableSlotSizes"
                  @buttonSelected="handleButtonSelected"
                  ref="buttonSelector"
                ></button-selector>

                <br />
                <div v-if="selectedButton" class="font-size-16" style="text-align: start">
                  <p class="margin-top-16 font-size-16">
                    {{ $t('lockerReturn.selectedSizeLabel') }}
                    <b class="font-weight-bold">{{ selectedButton.nameGreek }}</b
                    >.
                  </p>

                  {{ $t('lockerReturn.dimensionsLabel') }}
                  <b class="font-weight-bold">{{ selectedButton.dimensions }}</b>
                </div>
              </div>
              <div class="bottom-buttons-container">
                <div>
                  <button
                    :class="[
                      'merchantdrop-confirm-button',
                      { 'disable-confirm-button': disableConfirmBtn || isSubmitting },
                    ]"
                    @click="confirmOrder"
                    :disabled="disableConfirmBtn || isSubmitting"
                  >
                    {{ $t('lockerReturn.confirmButton') }}
                  </button>
                  <p class="merchantdrop-confirm-button-sub-text">
                    {{ $t('lockerReturn.confirmSubText') }}
                  </p>
                </div>
                <div v-if="showCallSupportBtn">
                  <a href="tel:+2111982107" style="">
                    <button class="call-support-button">
                      {{ $t('lockerReturn.callSupportButton') }}
                    </button>
                  </a>
                  <p class="merchantdrop-confirm-button-sub-text">
                    {{ $t('lockerReturn.callSupportSubText') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import axios from 'axios';

import Navbar from '../../pages-sections/Navbar.vue';
import Footer from '../../pages-sections/Footer.vue';
import MerchantDropoffSuccess from '../../pages/MerchantDropoff/MerchantDropoffSuccess.vue';
import Loader from '../../loader/Loader.vue';
import ButtonSelector from '../MerchantDropoff/ButtonSelector.vue';
import LockerReturnFail from './LockerReturnFail.vue';

export default {
  name: 'LockerReturn',
  components: {
    Navbar,
    Footer,
    Loader,
    MerchantDropoffSuccess,
    LockerReturnFail,
    ButtonSelector,
  },
  data() {
    return {
      token: '',
      isLoading: false,
      isSubmitting: false,
      isSuccess: null,
      selectedLockerExternalId: '',
      selectedLockerFriendlyName: '',
      selectedLockerAddress: '',
      selectedLockerModel: '',
      errorKey: 'generic',
      availableSlotSizes: [],
      selectedButton: null,
      disableConfirmBtn: true,
      showCallSupportBtn: false,
      showMap: false,
      isMobile: false,
    };
  },
  async mounted() {
    this.token = this.$route.params?.token;
    this.checkViewportWidth();
    window.addEventListener('resize', this.checkViewportWidth);

    const orderIsValid = await this.checkOrderStatus();
    if (orderIsValid) {
      this.initMap();
    }
  },
  methods: {
    checkViewportWidth() {
      this.isMobile = window.innerWidth < 800;
    },
    async checkOrderStatus() {
      try {
        this.isLoading = true;
        const response = await axios.get(
          `${process.env.VUE_APP_COURIER_API}/user/return_locker_selection/${this.token}`,
        );
        if (response.data?.locker_selected) {
          this.isSuccess = true;
          return false;
        }
        return true;
      } catch (error) {
        this.errorKey = this.getErrorKey(error);
        this.isSuccess = false;
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    async confirmOrder() {
      // Blocks double clicks: the selection can only be submitted once
      if (this.isSubmitting) {
        return;
      }
      this.isSubmitting = true;
      if (!window.confirm(this.$t('lockerReturn.confirmDialog'))) {
        this.isSubmitting = false;
        return;
      }
      try {
        this.isLoading = true;
        const response = await axios.post(
          `${process.env.VUE_APP_COURIER_API}/user/return_locker_selection`,
          {
            token: this.token,
            skroutz_point_id: this.selectedLockerExternalId,
            estimated_slot_size: this.selectedButton.size,
          },
        );
        this.isSuccess = response.status === 200;
      } catch (error) {
        this.errorKey = this.getErrorKey(error);
        this.isSuccess = this.errorKey === 'alreadySelected';
      } finally {
        this.isLoading = false;
      }
    },
    getErrorKey(error) {
      const errorKeys = {
        'token is required': 'tokenRequired',
        'Invalid or tampered token': 'invalidToken',
        'Locker selection link has expired': 'expiredLink',
        'Order not found': 'orderNotFound',
        'Order is not eligible for locker selection': 'notEligible',
        'Order has been cancelled': 'cancelledOrder',
        'Locker already selected for this order': 'alreadySelected',
        'Invalid or unavailable locker': 'unavailableLocker',
      };

      return errorKeys[error?.response?.data?.error] || 'generic';
    },
    initMap() {
      var _skroutzPointsMapWidgetOptions = {
        partnerId: 'skou',
        language: 'el',
        type: 'iframe',
        parentElement: '#skroutzLockerMap',
        viewType: 'dropoff',
        afterSelect: selected => {
          if (selected?.id) {
            this.selectedLockerExternalId = selected.id;
            this.selectedLockerFriendlyName = selected.name;
            this.selectedLockerAddress = `${selected.street_name} ${selected.street_number}, ${selected.city} ${selected.zip} `;

            this.selectedLockerModel = selected.model || '';
            this.availableSlotSizes = this.buildSlotSizes();
          } else if (selected === null) {
            this.selectedLockerExternalId = '';
            this.selectedLockerFriendlyName = '';
            this.selectedLockerAddress = '';
            this.selectedLockerModel = '';

            this.availableSlotSizes = [];

            this.resetButtonSelector();
          }
        },
      };
      window._skroutzPointsMapWidgetOptions = _skroutzPointsMapWidgetOptions;

      (function(d) {
        var e = d.createElement('script');
        e.src = `${process.env.VUE_APP_MAP}`;
        e.async = true;
        e.defer = true;
        d.getElementsByTagName('head')[0].appendChild(e);
      })(document);
    },
    buildSlotSizes() {
      if (!this.selectedLockerModel) {
        return [];
      }

      const sizes =
        this.selectedLockerModel === 'Cleveron'
          ? [
              { size: 'XS', nameKey: 'sizeXS', dims: { h: 12, w: 20, d: 62 } },
              { size: 'S', nameKey: 'sizeS', dims: { h: 12, w: 40, d: 62 } },
              { size: 'M', nameKey: 'sizeM', dims: { h: 20, w: 40, d: 62 } },
              { size: 'L', nameKey: 'sizeL', dims: { h: 38, w: 40, d: 62 } },
              { size: 'XL', nameKey: 'sizeXL', dims: { h: 60, w: 40, d: 62 } },
            ]
          : [
              { size: 'S', nameKey: 'sizeS', dims: { h: 11, w: 44, d: 59 } },
              { size: 'M', nameKey: 'sizeM', dims: { h: 24, w: 44, d: 59 } },
              { size: 'L', nameKey: 'sizeL', dims: { h: 50, w: 44, d: 59 } },
            ];

      const built = sizes.map(s => ({
        size: s.size,
        nameGreek: this.$t(`merchantDropoff.${s.nameKey}`),
        dimensions: this.$t('merchantDropoff.dimensionsFormat', s.dims),
      }));

      built.push({
        size: 'none',
        nameGreek: this.$t('merchantDropoff.sizeNone'),
        dimensions: '-',
      });

      return built;
    },
    handleButtonSelected(button) {
      this.selectedButton = button;

      if (button.size !== 'none') {
        this.disableConfirmBtn = false;
        this.showCallSupportBtn = false;
      } else {
        this.disableConfirmBtn = true;
        this.showCallSupportBtn = true;
      }
    },
    resetButtonSelector() {
      this.$refs.buttonSelector.resetSelection();

      this.disableConfirmBtn = true;
      this.showCallSupportBtn = false;
    },
    closeMap() {
      this.showMap = false;
    },
  },
  watch: {
    '$i18n.locale'() {
      if (!this.availableSlotSizes.length) {
        return;
      }

      const previousSize = this.selectedButton ? this.selectedButton.size : null;
      this.availableSlotSizes = this.buildSlotSizes();

      this.$nextTick(() => {
        const match = this.availableSlotSizes.find(s => s.size === previousSize);
        if (match && this.$refs.buttonSelector) {
          this.$refs.buttonSelector.selectButton(match);
        }
      });
    },
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkViewportWidth);
  },
};
</script>

<style scoped lang="scss">
.merchantdrop-title {
  margin-bottom: 10px;
  font-weight: 500;
  font-size: 26px;
}

.merchantdrop-main-container-loading {
  pointer-events: none;
  background: black;
  opacity: 30%;
}

.merchantdrop-grid-table {
  display: grid;
  grid-template-columns: 100%;
  gap: 10px;
  margin-top: 15px;
}

.merchantdrop-map {
  height: 600px;
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid black;
  border-radius: 5px;
  padding: 5px;
}

.merchantdrop-selected-locker-message {
  background-color: #f68b24;
  font-size: 16px;
  padding: 6px;
  line-height: 150%;
  text-align: center;
  color: white;
}

.merchantdrop-selected-highlight {
  font-weight: 700;
  color: white;
}

.bottom-buttons-container {
  display: flex;
  flex-direction: row;
  gap: 20px;
}

.merchantdrop-confirm-button {
  min-width: 150px;
  padding: 8px;
  font-size: 26px;
  text-align: center;
  background-color: #279a00;
  &:hover {
    background-color: green;
  }
  height: 55px;
}

.disable-confirm-button {
  cursor: not-allowed;
  background-color: #343c31;
  &:hover {
    background-color: #343c31;
  }

  color: grey;
}

.merchantdrop-confirm-button-sub-text {
  font-size: 12px;
  color: #000;
  width: 150px;
  margin-top: 1px;
  min-height: 100px;
}

.call-support-button {
  min-width: 150px;
  padding: 8px;
  font-size: 20px;
  height: 55px;
  text-align: center;
  background-color: #f68b24;
  &:hover {
    background-color: #f4a154;
  }
}

.show-map-button {
  display: none;
  margin: 20px 0;
}

@media screen and (max-width: 800px) {
  .merchantdrop-confirm-button {
    font-size: 22px;
    width: 150px;
    height: 60px;
  }
  .call-support-button {
    font-size: 18px;
    height: 60px;
  }

  .show-map-button {
    align-items: center;
    border: 2px solid #111111;
    border-radius: 4px;
    box-sizing: border-box;
    color: #111111;
    cursor: pointer;
    display: flex;
    justify-content: center;
    font-size: 16px;
    height: 60px;
    position: relative;
    text-align: center;
    text-decoration: none;
    user-select: none;
    -webkit-user-select: none;
    touch-action: manipulation;
    font-weight: 700;
    margin: 20px 8px;

    padding: 10px 20px;
    font-size: 20px;
    background-color: #ffffff;
    width: 200px;
  }

  .show-map-button:after {
    background-color: #e07f00;
    border-radius: 4px;
    content: '';
    display: block;
    height: 58px;
    left: -2px;
    width: 100%;
    position: absolute;
    top: -2px;
    transform: translate(8px, 8px);
    transition: transform 0.2s ease-out;
    z-index: -1;
  }

  .show-map-button:hover {
    background-color: #ffcc79;
  }

  .show-map-button:hover:after {
    transform: translate(0, 0);
  }

  .show-map-button:active {
    background-color: #ffdeda;
    outline: 0;
    font-weight: 400;
  }

  .popup-place-slm-map-container {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.6);
    z-index: 19997;
  }

  .popup-place-slm-map {
    width: 100vw;
    height: 100vh;
    top: 0;
    left: 0;
    transform: none;
    background-color: rgba(0, 0, 0, 0.5);
    padding-top: 20px;
  }
  .closeMapBtn {
    font-size: 50px;
    width: 50px;
    height: 50px;
    cursor: pointer;
    z-index: 20000;
    color: #f3b645;
  }

  .merchantdrop-map {
    height: 100vh;
  }
}

.loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
  cursor: wait;
}

.loader-overlay-hide {
  display: none;
}

.merchantdrop-container-main {
  flex-grow: 1;
  display: flex;
  background: url('../../../assets/images/graphic-bg-tracking.svg');
  background-repeat: no-repeat !important;
  background-position: center bottom;
}

@media screen and (max-width: 440px) {
  .bottom-buttons-container {
    .call-support-button {
      font-size: 18px;
      min-width: 140px;
      width: 140px;
    }
    .merchantdrop-confirm-button {
      font-size: 20px;
      min-width: 140px;
      width: 140px;
    }
    .merchantdrop-confirm-button-sub-text {
      font-size: 12px;
      width: 130px;
    }
  }
  .merchantdrop-container-main {
    padding: 56px 0 224px;
  }
}
@media screen and (max-width: 733px) {
  .merchantdrop-container-main {
    padding: 56px 0 224px;
  }
}

@media screen and (min-width: 733px) and (max-width: 961px) {
  .merchantdrop-container-main {
    padding: 136px 0 433px;
  }
}

@media screen and (max-width: 961px) {
  .merchantdrop-container-main {
    background: url('../../../assets/images/graphic-bg-tracking.svg') bottom center;
    justify-content: bottom;
    background-position: center bottom;
    background-repeat: no-repeat !important;
    width: 100%;

    min-height: 50vh;
  }
}

@media screen and (min-width: 961px) {
  .merchantdrop-container-main {
    padding: 146px 0 100px;
  }
}

.font-weight-bold {
  font-weight: 700;
}

.margin-top-16 {
  margin-top: 16px;
}

.margin-top-10 {
  margin-top: 10px;
}

.font-size-20 {
  font-size: 20px;
}

.font-size-16 {
  font-size: 16px;
}

.invisible {
  visibility: hidden !important;
}

.slot-size-container {
  margin: 50px 0 50px 0;
}
</style>
